/**
 * How many more attempts a group of RNG drops will take.
 *
 * The model: each attempt is ONE roll on the group's loot table, and item i
 * comes up with probability p_i — a Daily Op hands out one of nine plans, a
 * grave yields one thing or nothing. That is the classic coupon collector with
 * unequal weights, and both numbers the UI wants have exact inclusion–exclusion
 * forms over subsets S of the items still missing:
 *
 *   E[attempts]        = Σ_{S≠∅} (−1)^{|S|+1} / p(S)            p(S) = Σ_{i∈S} p_i
 *   P(all within n)    = Σ_{S}   (−1)^{|S|}   (1 − p(S))^n
 *
 * When the listed chances add up to more than 100% they cannot be one roll
 * (Head Hunts: five 4% plans plus a 95% legendary), so the items are treated
 * as independent rolls instead, where the same sums hold with
 * p(S) := 1 − Π_{i∈S}(1 − p_i) and P(all within n) = Π_i (1 − (1 − p_i)^n).
 *
 * Subset enumeration is 2^k; k is capped at 16 (65k subsets, sub-millisecond)
 * because no group here comes near it and a runaway is worse than no number.
 */

const MAX_ITEMS = 16;
const CONFIDENCE = 0.9;
/** Beyond this many attempts the answer is "not realistically". */
const MAX_ATTEMPTS = 10_000_000;

export type OddsModel = 'table' | 'independent';

export interface Odds {
  /** Items counted: still missing, with a known chance strictly between 0 and 1. */
  remaining: number;
  expected: number;
  /** Attempts for a 90% chance of having every one of them. */
  ninety: number;
  model: OddsModel;
}

/** "11.111%" → 0.11111, "100%" → 1. Anything else — "Journal page" — is null. */
export function parseChance(text: string | undefined): number | null {
  if (!text) return null;
  const m = /^\s*(\d+(?:[.,]\d+)?)\s*%\s*$/.exec(text);
  if (!m?.[1]) return null;
  const value = Number(m[1].replace(',', '.')) / 100;
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;
}

/** Null when there is nothing left to roll for, or too much to enumerate. */
export function oddsFor(chances: readonly number[]): Odds | null {
  const ps = chances.filter((p) => p > 0 && p < 1);
  if (ps.length === 0 || ps.length > MAX_ITEMS) return null;
  const sum = ps.reduce((a, b) => a + b, 0);
  const model: OddsModel = sum <= 1 + 1e-9 ? 'table' : 'independent';
  return {
    remaining: ps.length,
    expected: expectedAttempts(ps, model),
    ninety: attemptsFor(ps, model, CONFIDENCE),
    model,
  };
}

export function expectedAttempts(ps: readonly number[], model: OddsModel): number {
  let total = 0;
  forEachSubset(ps, model, (pS, size) => {
    total += (size % 2 === 1 ? 1 : -1) / pS;
  });
  return total;
}

export function collectAllWithin(ps: readonly number[], n: number, model: OddsModel): number {
  if (model === 'independent') {
    return ps.reduce((acc, p) => acc * (1 - (1 - p) ** n), 1);
  }
  let total = 1; // the empty subset
  forEachSubset(ps, model, (pS, size) => {
    total += (size % 2 === 1 ? -1 : 1) * (1 - pS) ** n;
  });
  // Cancellation across thousands of alternating terms can leave −1e−13.
  return Math.min(1, Math.max(0, total));
}

/** Smallest n with P(all within n) ≥ `confidence`. */
export function attemptsFor(ps: readonly number[], model: OddsModel, confidence: number): number {
  let hi = 1;
  while (collectAllWithin(ps, hi, model) < confidence) {
    if (hi >= MAX_ATTEMPTS) return MAX_ATTEMPTS;
    hi *= 2;
  }
  let lo = Math.floor(hi / 2);
  while (lo + 1 < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (collectAllWithin(ps, mid, model) >= confidence) hi = mid;
    else lo = mid;
  }
  return hi;
}

/** Calls `fn(p(S), |S|)` for every non-empty subset, with p(S) per the model. */
function forEachSubset(
  ps: readonly number[],
  model: OddsModel,
  fn: (pS: number, size: number) => void,
): void {
  const k = ps.length;
  for (let mask = 1; mask < 1 << k; mask += 1) {
    let sum = 0;
    let missProduct = 1;
    let size = 0;
    for (let i = 0; i < k; i += 1) {
      if (mask & (1 << i)) {
        const p = ps[i] ?? 0;
        sum += p;
        missProduct *= 1 - p;
        size += 1;
      }
    }
    fn(model === 'table' ? sum : 1 - missProduct, size);
  }
}
